import { ActivityType, SearchableActivityFilterInput } from '../../API.service.custom';

export const REQUIRED_TRIAL_REPRESENTATIVES = 3;
export const REQUIRED_TRIAL_TRACKED_CALLS = 500;

export interface TrialRewardMetricsInput {
  representativeCount: number;
  installedRepresentativeCount: number;
}

export interface TrialRewardProgress {
  representativeCount: number;
  installedRepresentativeCount: number;
  callCount: number;
  representativesComplete: boolean;
  installsComplete: boolean;
  callsComplete: boolean;
  completedRequirements: number;
  callProgressPercent: number;
  unlocked: boolean;
}

export interface TrialRewardCallFilterInput {
  accountId: string | null | undefined;
  trialStartedAt: string | null | undefined;
  representativeIds: Array<string | null | undefined>;
}

function wholeNonNegative(value: number | null | undefined): number {
  return Math.max(0, Math.floor(Number(value) || 0));
}

export function deriveTrialReward(
  metrics: TrialRewardMetricsInput,
  callCount: number | null | undefined,
): TrialRewardProgress {
  const representativeCount = wholeNonNegative(metrics?.representativeCount);
  const installedRepresentativeCount = wholeNonNegative(metrics?.installedRepresentativeCount);
  const normalizedCallCount = wholeNonNegative(callCount);
  const representativesComplete = representativeCount >= REQUIRED_TRIAL_REPRESENTATIVES;
  const installsComplete = installedRepresentativeCount >= REQUIRED_TRIAL_REPRESENTATIVES;
  const callsComplete = normalizedCallCount >= REQUIRED_TRIAL_TRACKED_CALLS;

  return {
    representativeCount,
    installedRepresentativeCount,
    callCount: normalizedCallCount,
    representativesComplete,
    installsComplete,
    callsComplete,
    completedRequirements: [representativesComplete, installsComplete, callsComplete].filter(Boolean).length,
    callProgressPercent: Math.min(100, Math.round((normalizedCallCount / REQUIRED_TRIAL_TRACKED_CALLS) * 100)),
    unlocked: representativesComplete && installsComplete && callsComplete,
  };
}

/**
 * Builds the single count-query filter used by the trial reward. Returning null
 * is intentional: without reps or a trustworthy trial start we must not count
 * account-wide or pre-trial activity.
 */
export function buildTrialRewardCallFilter(
  input: TrialRewardCallFilterInput,
): SearchableActivityFilterInput | null {
  const accountId = String(input?.accountId || '').trim();
  const trialStartedAt = String(input?.trialStartedAt || '').trim();
  const representativeIds = Array.from(new Set(
    (input?.representativeIds || [])
      .map(id => String(id || '').trim())
      .filter(Boolean),
  ));

  if (!accountId || !trialStartedAt || !Number.isFinite(Date.parse(trialStartedAt)) || representativeIds.length === 0) {
    return null;
  }

  return {
    accountId: { eq: accountId },
    activityType: { eq: ActivityType.CALL_LOGGED },
    createdAt: { gte: trialStartedAt },
    or: representativeIds.map(userId => ({ ownerId: { eq: userId } })),
  };
}
